import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import CustomerHeader from "@/components/CustomerHeader";
import Disclaimer from "@/components/Disclaimer";
import { Sparkles, ArrowLeft, Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { generateShareCard, downloadDataUrl, extractSnippet } from "@/lib/shareCard";

const TITLES = {
  kahve: "Kahve Falım",
  el: "El Falım",
  tarot: "Tarot Falım",
  ruya: "Rüya Yorumum",
  pirinc: "Pirinç Falım",
  iskambil: "İskambil Falım",
  katina: "Katina Falım",
  burc: "Günlük Burcum",
  ask: "Aşk Uyumum",
};

export default function FortuneResult() {
  const { id } = useParams();
  const [fortune, setFortune] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);

  useEffect(() => {
    api.get(`/fortunes/${id}`).then(({ data }) => setFortune(data));
  }, [id]);

  const buildCard = async () => {
    if (!fortune) return;
    setGenerating(true);
    try {
      const title = TITLES[fortune.fortune_type_slug] || "Falım";
      const snippet = extractSnippet(fortune.result_text || "", 320);
      const url = await generateShareCard({ title, snippet, name: "FalZilla" });
      setCardUrl(url);
    } catch (e) {
      toast.error("Kart oluşturulamadı");
    } finally { setGenerating(false); }
  };

  const share = async () => {
    if (!cardUrl) await buildCard();
    // Try native share with the blob
    try {
      const blob = await (await fetch(cardUrl || (await generateShareCard({
        title: TITLES[fortune.fortune_type_slug] || "Falım",
        snippet: extractSnippet(fortune.result_text || "", 320),
        name: "FalZilla",
      })))).blob();
      const file = new File([blob], "falzilla-story.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "FalZilla", text: "Falıma göz atın ✧" });
        return;
      }
    } catch (e) { /* fallback */ }
    // Fallback: download
    if (cardUrl) downloadDataUrl(cardUrl, "falzilla-story.png");
    toast.success("Görsel indirildi — Instagram/TikTok'a yükleyebilirsin!");
  };

  const download = async () => {
    let url = cardUrl;
    if (!url) {
      setGenerating(true);
      url = await generateShareCard({
        title: TITLES[fortune.fortune_type_slug] || "Falım",
        snippet: extractSnippet(fortune.result_text || "", 320),
        name: "FalZilla",
      });
      setCardUrl(url);
      setGenerating(false);
    }
    downloadDataUrl(url, "falzilla-story.png");
  };

  if (!fortune) return (
    <div className="cosmic-bg min-h-screen"><div className="stars" /><CustomerHeader /><div className="text-center py-20 text-white/60">Yükleniyor...</div></div>
  );

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/panel" className="text-white/60 text-sm hover:gold-text flex items-center gap-1" data-testid="result-back">
          <ArrowLeft className="w-4 h-4" /> Panele dön
        </Link>

        <div className="mt-6 fade-up">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 gold-text" />
            <span className="text-xs uppercase tracking-widest text-white/60">Yorumun hazır</span>
          </div>
          <h1 className="font-serif-myst text-4xl md:text-5xl mb-1">
            <span className="gold-text italic">{TITLES[fortune.fortune_type_slug] || fortune.fortune_type_slug}</span>
          </h1>
          <p className="text-white/50 text-sm">{new Date(fortune.created_at).toLocaleString("tr-TR")}</p>
        </div>

        <article data-testid="fortune-result-text" className="glass-strong rounded-3xl p-6 md:p-10 mt-8 fade-up">
          <div className="prose prose-invert max-w-none whitespace-pre-wrap font-[Manrope] text-white/90 leading-relaxed text-[15px] md:text-base">
            {fortune.result_text}
          </div>
        </article>

        {cardUrl && (
          <div className="mt-6 fade-up">
            <div className="text-xs uppercase tracking-widest text-white/60 mb-2 text-center">Instagram / TikTok Kartın</div>
            <img src={cardUrl} alt="Story kartı" className="rounded-2xl border gold-border max-h-[70vh] mx-auto" />
          </div>
        )}

        <div className="flex gap-3 mt-6 flex-wrap">
          <button onClick={buildCard} disabled={generating} data-testid="build-card-btn" className="btn-ghost flex-1 min-w-32 py-3 rounded-full flex items-center justify-center gap-2">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 gold-text" />}
            {cardUrl ? "Yeniden Oluştur" : "Story Kartı Yap"}
          </button>
          <button onClick={share} data-testid="share-result-btn" className="btn-ghost flex-1 min-w-32 py-3 rounded-full flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" /> Paylaş
          </button>
          <button onClick={download} data-testid="download-result-btn" className="btn-ghost flex-1 min-w-32 py-3 rounded-full flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> İndir
          </button>
          <Link to="/panel" data-testid="new-fortune-btn" className="btn-gold flex-1 min-w-32 py-3 rounded-full text-center">
            Yeni Fal
          </Link>
        </div>

        <Disclaimer />
      </main>
    </div>
  );
}
